interface StackedColumnChartProps {
  data: Array<{
    date: string;
    byStatus: { status: string; count: number }[];
  }>;
  getStatusColor: (status: string) => string;
  title?: string;
}

export function StackedColumnChart({ data, getStatusColor }: StackedColumnChartProps) {
  if (data.length === 0) {
    return (
      <div className='flex h-64 items-center justify-center text-gray-400 dark:text-gray-500'>
        No data available
      </div>
    );
  }

  // すべてのステータスを収集
  const allStatuses = Array.from(new Set(data.flatMap(d => d.byStatus.map(s => s.status)))).sort();

  // 各日の最大値を計算
  const maxTotal = Math.max(...data.map(d => d.byStatus.reduce((sum, s) => sum + s.count, 0)), 1);

  const chartHeight = 200;
  const chartWidth = 800;
  const barWidth = Math.max(12, Math.min(20, (chartWidth - 80) / data.length - 2));
  const padding = { top: 20, right: 20, bottom: 50, left: 40 };

  // Y軸のスケール
  const yScale = (value: number) =>
    chartHeight -
    padding.bottom -
    (value / maxTotal) * (chartHeight - padding.top - padding.bottom);

  // Y軸の目盛り
  const yTicks = [0, Math.ceil(maxTotal / 2), maxTotal];

  // 今日の日付
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className='flex flex-col'>
      <svg
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        className='h-64 w-full'
        preserveAspectRatio='xMidYMid meet'
      >
        {/* Y軸 */}
        <line
          x1={padding.left}
          y1={padding.top}
          x2={padding.left}
          y2={chartHeight - padding.bottom}
          stroke='currentColor'
          className='text-gray-300 dark:text-gray-600'
          strokeWidth='1'
        />

        {/* Y軸の目盛りとラベル */}
        {yTicks.map((tick, i) => (
          <g key={i}>
            <line
              x1={padding.left - 5}
              y1={yScale(tick)}
              x2={padding.left}
              y2={yScale(tick)}
              stroke='currentColor'
              className='text-gray-300 dark:text-gray-600'
              strokeWidth='1'
            />
            <text
              x={padding.left - 8}
              y={yScale(tick) + 4}
              textAnchor='end'
              className='fill-gray-500 text-[10px] dark:fill-gray-400'
            >
              {tick}
            </text>
            {/* グリッド線 */}
            <line
              x1={padding.left}
              y1={yScale(tick)}
              x2={chartWidth - padding.right}
              y2={yScale(tick)}
              stroke='currentColor'
              className='text-gray-200 dark:text-gray-700'
              strokeWidth='1'
              strokeDasharray='4,4'
            />
          </g>
        ))}

        {/* X軸 */}
        <line
          x1={padding.left}
          y1={chartHeight - padding.bottom}
          x2={chartWidth - padding.right}
          y2={chartHeight - padding.bottom}
          stroke='currentColor'
          className='text-gray-300 dark:text-gray-600'
          strokeWidth='1'
        />

        {/* 積み上げ棒グラフ */}
        {data.map((item, index) => {
          const x =
            padding.left +
            10 +
            index * ((chartWidth - padding.left - padding.right - 20) / data.length);
          const total = item.byStatus.reduce((sum, s) => sum + s.count, 0);

          // 日付ラベル
          const dateLabel = item.date.slice(5).replace('-', '/');
          const isToday = item.date === today;

          // 積み上げ位置を計算
          let cumulativeHeight = 0;

          return (
            <g key={index}>
              {/* 各ステータスの積み上げバー */}
              {item.byStatus.map((statusData, statusIndex) => {
                const segmentHeight =
                  (statusData.count / maxTotal) * (chartHeight - padding.top - padding.bottom);
                const y = chartHeight - padding.bottom - cumulativeHeight - segmentHeight;
                cumulativeHeight += segmentHeight;

                return (
                  <rect
                    key={statusIndex}
                    x={x}
                    y={y}
                    width={barWidth}
                    height={Math.max(segmentHeight, 0)}
                    fill={getStatusColor(statusData.status)}
                    className='transition-all hover:opacity-80'
                    rx='1'
                  >
                    <title>
                      {dateLabel}: {statusData.status} - {statusData.count}
                    </title>
                  </rect>
                );
              })}

              {/* 合計値ラベル（バーの上部、値が0より大きい場合のみ） */}
              {total > 0 && (
                <text
                  x={x + barWidth / 2}
                  y={chartHeight - padding.bottom - cumulativeHeight - 4}
                  textAnchor='middle'
                  className='fill-gray-600 text-[8px] font-medium dark:fill-gray-300'
                >
                  {total}
                </text>
              )}

              {/* X軸ラベル（5日ごと、または今日） */}
              {(index % 5 === 0 || isToday) && (
                <text
                  x={x + barWidth / 2}
                  y={chartHeight - padding.bottom + 14}
                  textAnchor='middle'
                  className={`text-[9px] ${
                    isToday
                      ? 'fill-blue-600 font-bold dark:fill-blue-400'
                      : 'fill-gray-500 dark:fill-gray-400'
                  }`}
                >
                  {dateLabel}
                </text>
              )}

              {/* 今日のマーカー */}
              {isToday && (
                <line
                  x1={x + barWidth / 2}
                  y1={padding.top}
                  x2={x + barWidth / 2}
                  y2={chartHeight - padding.bottom}
                  stroke='currentColor'
                  className='text-blue-400 dark:text-blue-500'
                  strokeWidth='2'
                  strokeDasharray='4,4'
                  opacity='0.5'
                />
              )}
            </g>
          );
        })}
      </svg>

      {/* 凡例 */}
      <div className='mt-4 flex flex-wrap justify-center gap-3'>
        {allStatuses.map(status => (
          <div key={status} className='flex items-center gap-1.5'>
            <div
              className='h-3 w-3 rounded-sm'
              style={{ backgroundColor: getStatusColor(status) }}
            />
            <span className='text-xs font-medium text-gray-700 dark:text-gray-200'>{status}</span>
          </div>
        ))}
      </div>

      {/* サマリー */}
      <div className='mt-2 text-center text-sm text-gray-600 dark:text-gray-400'>
        Past 15 days · Today · Next 15 days
      </div>
    </div>
  );
}
